import { useState } from 'react';
import { Link } from 'react-router-dom';

function MoreMenu() {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        inventory: true,
        sales: true,
        purchasing: false,
        logistics: false,
        accounting: false,
        delivery: false,
        general: false,
        settings: false
    });

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const quickActions = [
        { path: '/products/manage', label: 'Products', icon: 'M12 4v16m8-8H4', color: 'indigo', gradient: 'from-indigo-500 to-purple-600' },
        { path: '/restocks/new', label: 'Restock', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', color: 'emerald', gradient: 'from-emerald-500 to-teal-600' },
        { path: '/orders/manage', label: 'New Order', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', color: 'amber', gradient: 'from-amber-500 to-orange-600' },
        { path: '/invoices/new', label: 'New Invoice', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z', color: 'rose', gradient: 'from-rose-500 to-pink-600' }
    ];

    const inventoryLinks = [
        { path: '/products', label: 'Products', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
        { path: '/categories', label: 'Categories', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
        { path: '/product_units', label: 'Serial Numbers', icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14' },
        { path: '/restocks', label: 'Restock History', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' }
    ];

    const salesLinks = [
        { path: '/invoices', label: 'Invoices', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z' },
        { path: '/orders', label: 'Orders', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { path: '/returns', label: 'Returns', icon: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6' },
        { path: '/credit-notes', label: 'Credit Notes', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { path: '/customers', label: 'Customers', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }
    ];

    const purchasingLinks = [
        { path: '/purchases', label: 'Purchases', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' },
        { path: '/vendors', label: 'Vendors', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' }
    ];

    const logisticsLinks = [
        { path: '/finance_companies', label: 'Finance Companies', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { path: '/insurance_companies', label: 'Insurance Companies', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        ...((window as any).CURRENT_FIRM?.industry === 'vehicle' ? [{ path: '/rto_agents', label: 'RTO Agents', icon: 'M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z' }] : []),
        { path: '/exchange_dealers', label: 'Exchange Dealers', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
        { path: '/locations', label: 'Logistics Hubs', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' }
    ];
    
    const deliveryLinks = [
        { path: '/delivery/routes', label: 'Delivery Routes', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
        { path: '/delivery/subscriptions', label: 'Subscriptions', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
        { path: '/delivery/assignments', label: 'Assignments', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }
    ];

    const accountingLinks = [
        { path: '/accounting', label: 'Dashboard', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
        { path: '/accounting/account-groups', label: 'Account Groups', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
        { path: '/accounting/account-heads', label: 'Account Heads', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
        { path: '/accounting/bank-accounts', label: 'Bank Accounts', icon: 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z' },
        { path: '/accounting/vouchers', label: 'Vouchers', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }
    ];

    const settingsLinks = [
        { path: '/firms', label: 'Companies', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
        { path: '/settings/modules', label: 'Modules', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
        { path: '/settings/roles', label: 'Roles & Permissions', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
        { path: '/staff', label: 'Staff', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
        { path: '/settings/product-fields', label: 'Product Fields', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }
    ];

    const otherLinks = [
        { path: '/profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
        { path: '/calendar', label: 'Calendar', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
        { path: '/store', label: 'Store Config', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
        { path: '/store/analytics', label: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
    ];

    const renderCollapsibleSection = (title: string, emoji: string, links: any[], sectionKey: string) => (
        <div className="mb-4 animate-fade-in">
            <button
                onClick={() => toggleSection(sectionKey)}
                className="w-full flex items-center justify-between p-4 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl hover:shadow-md transition-all active:scale-[0.98] mb-3"
            >
                <div className="flex items-center gap-3">
                    <span className="text-2xl">{emoji}</span>
                    <h3 className="text-base font-bold text-slate-800">{title}</h3>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-medium">{links.length}</span>
                </div>
                <svg
                    className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${expandedSections[sectionKey] ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {expandedSections[sectionKey] && (
                <div className="grid grid-cols-2 gap-3 animate-slide-down">
                    {links.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className="group flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-300 hover:shadow-lg hover:scale-[1.02] transition-all active:scale-[0.98]"
                        >
                            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 text-slate-600 rounded-xl group-hover:from-indigo-50 group-hover:to-purple-50 group-hover:text-indigo-600 transition-all">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={link.icon} />
                                </svg>
                            </div>
                            <span className="text-sm font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">{link.label}</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/20 to-slate-50 pb-24 lg:pb-8">
            <div className="px-4 py-6 max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 animate-fade-in">
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">Menu</h1>
                    <p className="text-slate-500">Access all features and settings</p>
                </div>

                {/* Quick Actions */}
                <div className="mb-8 animate-fade-in">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-1">⚡ Quick Actions</h3>
                    <div className="grid grid-cols-2 gap-4">
                        {quickActions.map((action) => (
                            <Link
                                key={action.path}
                                to={action.path}
                                className="group relative overflow-hidden flex flex-col items-center justify-center p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-[0.95]"
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-90 group-hover:opacity-100 transition-opacity`}></div>
                                <div className="relative z-10 flex flex-col items-center">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={action.icon} />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-bold text-white">{action.label}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Collapsible Sections */}
                {renderCollapsibleSection('Inventory', '📦', inventoryLinks, 'inventory')}
                {renderCollapsibleSection('Sales', '💰', salesLinks, 'sales')}
                {renderCollapsibleSection('Purchasing', '🛒', purchasingLinks, 'purchasing')}
                {renderCollapsibleSection('Logistics', '🚚', logisticsLinks, 'logistics')}
                {renderCollapsibleSection('Accounting', '📊', accountingLinks, 'accounting')}
                {(window as any).CURRENT_FIRM?.industry === 'dairy' && renderCollapsibleSection('Milk & Delivery', '🥛', deliveryLinks, 'delivery')}
                {renderCollapsibleSection('General', '⚙️', otherLinks, 'general')}
                {renderCollapsibleSection('Settings', '🔧', settingsLinks, 'settings')}

                {/* Logout Button */}
                <div className="mt-8 animate-fade-in">
                    <button
                        onClick={async () => {
                            try {
                                const csrfToken = (window as any).CSRF_TOKEN || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                                await fetch('/users/sign_out', {
                                    method: 'DELETE',
                                    headers: {
                                        'X-CSRF-Token': csrfToken,
                                        'Content-Type': 'application/json'
                                    }
                                });
                                // Clear local storage
                                localStorage.removeItem('token');
                                localStorage.removeItem('user');
                                localStorage.removeItem('user_role');
                                localStorage.removeItem('current_firm_id');
                                localStorage.removeItem('demo_mode');
                                
                                window.location.href = '/login';
                            } catch (error) {
                                console.error('Logout failed:', error);
                                window.location.href = '/';
                            }
                        }}
                        className="w-full flex items-center justify-center gap-3 p-4 bg-white border-2 border-rose-200 rounded-2xl text-rose-600 font-bold hover:bg-rose-50 hover:border-rose-300 hover:shadow-lg transition-all active:scale-[0.98]"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Log Out
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-down {
                    from { 
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }
                .animate-slide-down {
                    animation: slide-down 0.3s ease-out;
                }
            `}</style>
        </div>
    );
}

export default MoreMenu;
